import { FEATURE_FLAGS } from '../consts/features';
import { useConfigQuery } from '../network/queries/useConfigQuery';
import { useFeatureAccess } from './useFeatureAccess';

type WikiCapabilityAccess = {
  isEnabled: boolean;
  canAccess: boolean;
  isLoading: boolean;
};

/**
 * Whether the Qodo Wiki capability should be surfaced for the current
 * workspace. Combines the platform rollout flag (FEATURE_FLAGS.WIKI) with the
 * workspace-level `wiki.enabled` config toggle.
 *
 * The config toggle is default-on, so only an explicit `wiki.enabled === false`
 * hides the capability; a missing key or a failed config read falls back to
 * enabled (mirrors the backend's fail-open generation gate).
 */
export const useWikiCapabilityEnabled = (): WikiCapabilityAccess => {
  const {
    isEnabled: isFeatureEnabled,
    canAccess: canAccessFeature,
    isLoading: isFeatureLoading,
  } = useFeatureAccess({ featureId: FEATURE_FLAGS.WIKI });
  const { data, isLoading: isConfigLoading } = useConfigQuery();

  const wikiConfig = data?.config?.wiki as { enabled?: boolean } | undefined;
  const isConfigEnabled = wikiConfig?.enabled !== false;

  return {
    isEnabled: isFeatureEnabled && isConfigEnabled,
    canAccess: canAccessFeature && isConfigEnabled,
    isLoading: isFeatureLoading || isConfigLoading,
  };
};
